// /profile (Account → Profile): you (first name, surname) and your company's
// details. The company details are also the starting point for every hosted
// page's header and footer (new hosted tools copy them; the Hosted page tab can
// fill blanks from them). API: 100-api functions/profile.
// Base postcode (Neil, 11 Oct 2026): where the company works from. Find shows each property's
// distance from it as a band (never miles or direction). Checked on the server as a full UK
// postcode we can find; it can only be changed a few times a day.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Check } from "lucide-react";
import { useApi, type CompanyDetails, type CompanyProfile } from "../api";
import { Field } from "../components/controls";
import { GoogleGlyph, MicrosoftGlyph } from "../components/SsoGlyphs";
import { useAuth } from "../auth/AuthContext";
import "../widgets.css";

// Company fields: key, label, hint, input type, autocomplete (same set as a hosted page)
export const COMPANY_FIELDS: [keyof CompanyDetails, string, string, string, string][] = [
  ["companyName", "Company name", "Your trading name, as customers know you.", "text", "organization"],
  ["tagline", "Tagline", "A short line, e.g. “Commercial PV across West Yorkshire”.", "text", "off"],
  ["phone", "Phone", "The number customers should call.", "tel", "tel"],
  ["email", "Email", "The address customers should write to.", "email", "email"],
  ["website", "Website", "Starts with https://", "url", "url"],
  ["address", "Address", "One line.", "text", "street-address"],
  ["companyNumber", "Company number", "", "text", "off"],
  ["vatNumber", "VAT number", "", "text", "off"],
  ["privacyUrl", "Privacy policy link", "Starts with https://", "url", "off"],
];

/** "Signed up with" badge: Google / Microsoft logo for SSO, the xenscope icon for email & password. */
function SignInMethod({ provider }: { provider: string }) {
  const sso: Record<string, [() => ReactNode, string]> = { Google: [GoogleGlyph, "Google"], Microsoft: [MicrosoftGlyph, "Microsoft"] };
  const [Glyph, label] = sso[provider] ?? [null, "xenscope Commercial email & password"];
  return (
    <span className="signin-method" title={`Signs in with ${label}`}>
      {Glyph ? <Glyph /> : <img src="/favicon-32x32.png" alt="" />}
      {label}
    </span>
  );
}

export function ProfilePage() {
  const api = useApi();
  const { user } = useAuth();
  const [saved, setSaved] = useState<CompanyProfile | null>(null);
  const [draft, setDraft] = useState<CompanyProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  // Load once (an older API without a base postcode reads as "not set")
  useEffect(() => {
    api
      .getProfile()
      .then((p) => {
        const loaded = { ...p, base: p.base ?? { postcode: "" } };
        setSaved(loaded);
        setDraft(loaded);
      })
      .catch((e) => setMessage({ tone: "danger", text: (e as Error).message }));
  }, [api]);

  const dirty = saved !== null && draft !== null && JSON.stringify(saved) !== JSON.stringify(draft);

  const setUser = (patch: Partial<CompanyProfile["user"]>) => {
    setDraft((d) => (d ? { ...d, user: { ...d.user, ...patch } } : d));
    setMessage(null);
  };
  const setCompany = (patch: Partial<CompanyDetails>) => {
    setDraft((d) => (d ? { ...d, company: { ...d.company, ...patch } } : d));
    setMessage(null);
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    if (!draft.company.companyName.trim()) return setMessage({ tone: "danger", text: "Enter your company name." });
    setSaving(true);
    setMessage(null);
    try {
      const p = await api.updateProfile({
        user: { firstName: draft.user.firstName.trim(), lastName: draft.user.lastName.trim() },
        company: draft.company,
        // Sent every time; the server ignores it when unchanged
        base: { postcode: (draft.base?.postcode ?? "").trim() },
      });
      const updated = { ...p, base: p.base ?? { postcode: "" } };
      setSaved(updated);
      setDraft(updated);
      setMessage({ tone: "success", text: "Profile saved." });
    } catch (err) {
      setMessage({ tone: "danger", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Profile</h1>
        <p>You and your company. Your company name goes on every report we prepare for you.</p>
      </div>

      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      {draft === null ? (
        !message && <p className="muted">Loading…</p>
      ) : (
        <form className="profile" onSubmit={(e) => void save(e)}>
          {/* You */}
          <section className="card profile-card">
            <div className="profile-you-head">
              {/* Photo from SSO (Google), when signed in that way */}
              {draft.user.pictureUrl && <img className="profile-avatar" src={draft.user.pictureUrl} alt="" referrerPolicy="no-referrer" />}
              <h2>You</h2>
            </div>
            <div className="profile-grid">
              <Field label="First name" htmlFor="first-name">
                <input id="first-name" className="input" value={draft.user.firstName} maxLength={60} autoComplete="given-name" onChange={(e) => setUser({ firstName: e.target.value })} />
              </Field>
              <Field label="Surname" htmlFor="last-name">
                <input id="last-name" className="input" value={draft.user.lastName} maxLength={60} autoComplete="family-name" onChange={(e) => setUser({ lastName: e.target.value })} />
              </Field>
            </div>
            <Field label="Sign-in email">
              <p className="profile-static profile-signin">
                {draft.user.email}
                {/* How this account signs in: the provider's logo and name */}
                <SignInMethod provider={user?.provider ?? "email"} />
              </p>
            </Field>
          </section>

          {/* Company */}
          <section className="card profile-card">
            <h2>Company</h2>
            <div className="profile-grid">
              {COMPANY_FIELDS.map(([key, label, hint, type, auto]) => (
                <div key={key} className={key === "address" || key === "tagline" ? "profile-wide" : undefined}>
                  <Field label={label} htmlFor={`co-${key}`} hint={hint || undefined}>
                    <input
                      id={`co-${key}`}
                      className="input"
                      type={type}
                      value={draft.company[key]}
                      autoComplete={auto}
                      placeholder={key === "website" || key === "privacyUrl" ? "https://" : undefined}
                      onChange={(e) => setCompany({ [key]: e.target.value })}
                    />
                  </Field>
                </div>
              ))}
            </div>
            {draft.ownerEmail && <p className="profile-static">Account owner: {draft.ownerEmail}</p>}
          </section>

          {/* Base postcode: Find measures distance bands from it */}
          <section className="card profile-card">
            <h2>Base postcode</h2>
            <div className="profile-grid">
              <Field
                label="Postcode you work from"
                htmlFor="base-postcode"
                hint="A full postcode, e.g. LS2 9JT. Find shows how far each property is from here, as a band. You can change it a few times a day."
              >
                <input
                  id="base-postcode"
                  className="input"
                  value={draft.base?.postcode ?? ""}
                  maxLength={10}
                  autoComplete="postal-code"
                  onChange={(e) => {
                    setDraft((d) => (d ? { ...d, base: { postcode: e.target.value.toUpperCase() } } : d));
                    setMessage(null);
                  }}
                />
              </Field>
            </div>
          </section>

          <div className="profile-actions">
            <button type="submit" className="btn btn-primary" disabled={!dirty || saving}>
              <Check size={16} aria-hidden="true" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
